import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { useEffect, useMemo, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { apiInstance } from "@repo/core/services/instances/api";
import { ISelectOption } from "@repo/core/components/core/Select";
import { usePreProposal } from "../../hooks/usePreProposal";
import useToast from "@repo/core/components/core/Toast";
import useAuthStore from "@repo/core/stores/auth";
import { persistPreProposalAlterarDataConferencePendingOrThrow } from "../../services/persistPreProposalAlterarPending.service";
import { usePreProposalStore } from "../../store/preProposalStore";
import { useLayoutBandeiras } from "@repo/core/hooks/useLayoutBandeiras";
import { useRequirePreProposalProduct } from "../../hooks/useRequirePreProposalProduct";
import {
  normalizeLayoutBandeirasProductParam,
  shouldFetchLayoutBandeirasWhenBandeiraMissing
} from "@repo/core/utils/layoutBandeiras.utils";

const proposalsResource = new ProposalsResource(apiInstance);

export const SelectAttendantController = () => {
  const { back, push, replace } = useNavigation();
  const toast = useToast();
  const { user: userAuth } = useAuthStore();

  const [selectedAttendant, setSelectedAttendant] = useState<null | string>(
    null
  );
  const [isReady, setIsReady] = useState(false);
  const hasAutoNavigated = useRef(false);

  const { proposalData, setProposalData } = usePreProposal();

  const selectedProduct = normalizeLayoutBandeirasProductParam(
    proposalData?.strProduto
  );
  const hasProduct = useRequirePreProposalProduct(selectedProduct);
  const shouldLoadFlags = shouldFetchLayoutBandeirasWhenBandeiraMissing(
    selectedProduct,
    proposalData?.strBandeira
  );
  const shouldLoadAttendants =
    hasProduct &&
    !!proposalData?.strPontoAtendimento &&
    !!proposalData?.strBandeira;

  const flagsQuery = useLayoutBandeiras(selectedProduct, shouldLoadFlags);

  const attendantsQuery = useQuery({
    queryKey: [
      "proposal-attendants",
      selectedProduct,
      proposalData?.strPontoAtendimento,
      proposalData?.strBandeira
    ],
    queryFn: () =>
      proposalsResource.fetchAttendants({
        strProduto: selectedProduct,
        strBandeira: proposalData?.strBandeira || "",
        strPontoAtendimento: proposalData?.strPontoAtendimento || ""
      }),
    select: (data) => data.data,
    enabled: shouldLoadAttendants
  });

  useEffect(() => {
    const flagsReady =
      !shouldLoadFlags ||
      (flagsQuery.isFetched && !flagsQuery.isLoading && !flagsQuery.isFetching);
    const attendantsReady =
      !shouldLoadAttendants ||
      (attendantsQuery.isFetched &&
        !attendantsQuery.isLoading &&
        !attendantsQuery.isFetching);

    // Only mark as ready if:
    // 1. Flags have been loaded (or don't need to be loaded)
    // 2. Attendants have been loaded (or don't need to be loaded)
    // 3. If flags needed to be loaded, the flag must already be defined
    const bandeiraDefined = !shouldLoadFlags || !!proposalData?.strBandeira;

    if (hasProduct && flagsReady && attendantsReady && bandeiraDefined) {
      setIsReady(true);
    }
  }, [
    hasProduct,
    shouldLoadFlags,
    shouldLoadAttendants,
    flagsQuery.isFetched,
    flagsQuery.isLoading,
    flagsQuery.isFetching,
    attendantsQuery.isFetched,
    attendantsQuery.isLoading,
    attendantsQuery.isFetching,
    proposalData?.strBandeira
  ]);

  const attendants = useMemo<ISelectOption[]>(
    () =>
      attendantsQuery.data?.map((attendant) => ({
        value: attendant.atendente,
        label: attendant.nome
      })) || [],
    [attendantsQuery.data]
  );

  const attendantInfo = useMemo(() => {
    return attendants?.find(
      (attendant) => attendant.value === selectedAttendant
    );
  }, [selectedAttendant, attendants]);

  const handleSelectAttendant = (id: string | string[]) => {
    setSelectedAttendant(id as string);
  };

  const handleOnSubmitForm = async () => {
    if (!selectedAttendant) {
      toast.error(
        "Atendente não selecionado",
        "Por favor, selecione um atendente"
      );
      return;
    }

    setProposalData(
      { strAtendente: selectedAttendant },
      { skipAlterarSync: true }
    );

    const latestSnapshot = usePreProposalStore.getState().proposalData;
    if (latestSnapshot?.lngProposta) {
      try {
        await persistPreProposalAlterarDataConferencePendingOrThrow(
          latestSnapshot
        );
      } catch {
        toast.error(
          "Erro ao salvar etapa",
          "Não foi possível sincronizar com o servidor. Tente novamente."
        );
        return;
      }
    }

    push(ROUTES.preProposalsInfoSecurity);
  };

  useEffect(() => {
    if (!proposalData?.strBandeira && flagsQuery.data?.length) {
      setProposalData(
        { strBandeira: flagsQuery.data?.[0]?.strBandeira },
        { skipAlterarSync: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsQuery.data, proposalData?.strBandeira]);

  // Auto-navigate when there's only one attendant
  useEffect(() => {
    if (
      attendantsQuery.data?.length === 1 &&
      isReady &&
      attendantsQuery.isFetched &&
      !hasAutoNavigated.current
    ) {
      hasAutoNavigated.current = true;
      const singleAttendant = attendantsQuery.data[0].atendente;

      // Save data first
      setProposalData(
        { strAtendente: singleAttendant },
        { skipAlterarSync: true }
      );
      setSelectedAttendant(singleAttendant);

      // Navigate immediately using replace to avoid back button issues
      replace(ROUTES.preProposalsInfoSecurity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendantsQuery.data, isReady, attendantsQuery.isFetched, replace]);

  // Try to find attendant by user name (only if multiple attendants)
  useEffect(() => {
    if (
      attendants?.length &&
      attendants.length > 1 &&
      !selectedAttendant &&
      isReady &&
      !hasAutoNavigated.current
    ) {
      const findAttendant = attendants.find(
        (attendant) => attendant.label === userAuth?.name
      );
      if (findAttendant) setSelectedAttendant(findAttendant.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendants, userAuth?.name, selectedAttendant, isReady]);

  return {
    back,
    attendantInfo,
    handleOnSubmitForm,
    attendants,
    handleSelectAttendant,
    isFetched: isReady && attendantsQuery.data?.length !== 1
  };
};
